import { useTopRowOverContext } from '@/components/DraggableTable/context/TopRowOverProvider';
import {
  defaultDropAnimation,
  defaultDropAnimationSideEffects,
  DragOverlay,
} from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { useSortable } from '@dnd-kit/sortable';
import classNames from 'classnames';
import {
  createContext,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import styles from './Row.module.scss';
const cx = classNames.bind(styles);

export interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string | number;
  selectedRowId: number | string | null;
  shownMenuId: number | string | null;
  setSelectedRowId: (id: number | string | null) => void;
  setShownMenuId: (id: number | string | null) => void;
}

export interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
}

export const RowContext = createContext<RowContextProps>({});

export const Row: FC<RowProps> = ({
  selectedRowId,
  shownMenuId,
  setSelectedRowId,
  setShownMenuId,
  ...props
}: RowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    isDragging,
    active,
    over,
  } = useSortable({ id: props['data-row-key'] });

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );

  const trRef = useRef<HTMLTableRowElement>(null);

  const dropAnimation = useMemo(() => {
    return {
      ...defaultDropAnimation,
      sideEffects: defaultDropAnimationSideEffects({
        styles: {
          active: {
            opacity: '1',
          },
        },
      }),
    };
  }, []);

  const { isShowTop, getHoverPosition } = useTopRowOverContext();

  const onClickRow = useCallback(() => {
    if (selectedRowId && selectedRowId === props['data-row-key']) {
      setSelectedRowId(null);
    } else {
      setSelectedRowId(props['data-row-key']);
    }
    setShownMenuId(null);
  }, [props, selectedRowId, setSelectedRowId, setShownMenuId]);

  const getRowStyle = useCallback(() => {
    return {
      [styles['draggable-table-row-over-top']]:
        isShowTop &&
        active?.data.current?.sortable.items[0] === props['data-row-key'] &&
        over?.id !== active?.id &&
        over?.id === props['data-row-key'],

      [styles['draggable-table-row-over']]:
        over?.id !== active?.id && over?.id === props['data-row-key'],
    };
  }, [isShowTop, active, over, props]);

  useEffect(() => {
    if (
      isDragging &&
      over &&
      over?.id === active?.data.current?.sortable.items[0]
    ) {
      requestAnimationFrame(() => {
        getHoverPosition(over, trRef);
      });
    }
  }, [
    isShowTop,
    isDragging,
    getHoverPosition,
    transform,
    selectedRowId,
    props,
    active?.data,
    over?.id,
    over,
  ]);

  return (
    <RowContext.Provider value={contextValue}>
      {isDragging && active?.id === props['data-row-key'] && (
        <tr className={styles['hide']}>
          <td className={styles['hide']}>
            <DragOverlay
              dropAnimation={dropAnimation}
              wrapperElement='table'
              className={cx(styles['draggable-table-row-dragging'])}
            >
              <tbody>
                <tr ref={trRef}>{props.children}</tr>
              </tbody>
            </DragOverlay>
          </td>
        </tr>
      )}
      <tr
        {...props}
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        onClick={onClickRow}
        className={cx({
          ...getRowStyle(),
          [styles['draggable-table-row-selected']]:
            props['data-row-key'] === String(selectedRowId),
          [styles['draggable-table-row-shown-menu']]:
            props['data-row-key'] === String(shownMenuId),
          [styles['draggable-active']]:
            isDragging && props['data-row-key'] === String(active?.id),
        })}
      >
        {props.children}
      </tr>
    </RowContext.Provider>
  );
};
